import { DEFAULT_SETTINGS, type Settings, type UpdateSettings } from "@ronin/contracts";
import { toast } from "@ronin/ui";
import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../api.js";
import { showError } from "../../lib/errors.js";

export const settingsQuery = queryOptions({
  queryKey: ["settings"],
  queryFn: () => api.settings.get(),
  staleTime: 5 * 60_000,
});

/**
 * Settings and the save mutation. Each change is shown at once and saved in the background;
 * on an error the old value comes back (spec02 FR2).
 */
export function useSettings() {
  const queryClient = useQueryClient();
  const query = useQuery(settingsQuery);

  const update = useMutation({
    mutationFn: (patch: UpdateSettings) => api.settings.update(patch),
    onMutate: async (patch) => {
      await queryClient.cancelQueries({ queryKey: settingsQuery.queryKey });
      const previous = queryClient.getQueryData<Settings>(settingsQuery.queryKey);
      if (previous) queryClient.setQueryData(settingsQuery.queryKey, { ...previous, ...patch });
      return { previous };
    },
    onError: (error, _patch, context) => {
      if (context?.previous) queryClient.setQueryData(settingsQuery.queryKey, context.previous);
      showError("the setting")(error);
    },
    onSuccess: (settings) => {
      queryClient.setQueryData(settingsQuery.queryKey, settings);
      // Timezone and holiday settings change what the calendar shows.
      void queryClient.invalidateQueries({ queryKey: ["calendar"] });
      toast.success("Saved");
    },
  });

  return { query, update };
}

/** The user's display timezone; the default until settings have loaded. */
export function useTimeZone(): string {
  return useQuery(settingsQuery).data?.timezone ?? DEFAULT_SETTINGS.timezone;
}

/** Attachment storage (spec05 FR4); uploads invalidate it. */
export const attachmentUsageQuery = queryOptions({
  queryKey: ["attachments", "usage"],
  queryFn: () => api.attachments.usage(),
});
